import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, DownloadSimple, Info, CheckCircle } from "@phosphor-icons/react";

export default function AnnualReport() {
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/annual-report/pdf?year=${year}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success(`Laporan Tahunan ${year} dibuat`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat laporan tahunan");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl space-y-5" data-testid="annual-report-page">
      <div>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-medium">Laporan Tahunan PT</h1>
        <p className="text-sm text-stone-500 mt-1">
          Laporan tahunan resmi untuk update laporan keuangan penanaman modal dan lampiran SPT Badan —
          lengkap dengan Catatan Atas Laporan Keuangan (CALK) yang tersusun otomatis dari saldo Neraca & Laba Rugi.
        </p>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="annual-download-card">
        <h3 className="font-serif-display text-xl font-medium mb-4 flex items-center gap-2">
          <FileText size={18} className="text-blue-700" />Unduh Laporan Tahunan
        </h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Tahun Buku</Label>
            <Input type="number" className="w-28" value={year}
                   onChange={(e) => setYear(Number(e.target.value) || year)}
                   data-testid="annual-year-input" />
          </div>
          <Button onClick={download} disabled={downloading}
                  className="bg-blue-700 hover:bg-blue-800 text-white h-10"
                  data-testid="annual-download-btn">
            <DownloadSimple size={15} className="mr-1" />
            {downloading ? "Menyusun PDF…" : `Unduh Laporan Tahunan ${year}`}
          </Button>
        </div>
      </div>

      <div className="bg-stone-50 border border-stone-200 rounded-lg p-5 text-sm text-stone-600 leading-relaxed" data-testid="annual-info-card">
        <div className="font-medium text-stone-800 mb-2 flex items-center gap-1.5">
          <Info size={14} className="text-blue-700" />Isi laporan (mengikuti format Laporan Tahunan PT):
        </div>
        <ul className="space-y-1.5 text-xs sm:text-sm">
          {[
            "Halaman sampul dengan logo, nama, dan NPWP Perseroan",
            "Profil singkat perseroan + tabel KBLI",
            "Ikhtisar laporan keuangan: Neraca (2 tahun pembanding), Laba Rugi, Arus Kas, Perubahan Ekuitas",
            "Catatan Atas Laporan Keuangan (CALK) format SAK ETAP — narasi kebijakan akuntansi + rincian saldo per akun dari Neraca & Laba Rugi",
            "Laporan kegiatan perseroan, tanggung jawab sosial & lingkungan, pengawasan Dewan Komisaris",
            "Susunan Direksi & Dewan Komisaris + halaman tanda tangan",
          ].map((t, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle size={13} className="text-emerald-600 mt-0.5 shrink-0" weight="fill" />{t}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-stone-500">
          Data profil perusahaan (kedudukan, akta pendirian, SK Menkumham, KBLI, susunan Direksi/Komisaris)
          diisi di <b>Pengaturan → Profil Perusahaan → Data Korporasi</b>. Angka keuangan diambil otomatis
          dari pembukuan tahun buku yang dipilih.
        </p>
      </div>
    </div>
  );
}
